import { useParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { Sparkles, MapPin, Twitter, Linkedin, Instagram, ExternalLink, ShieldCheck, Award, FileText, Newspaper } from 'lucide-react'
import { ConorbitLoader } from '@/components/ui/ConorbitLoader'

const BADGE_CONFIG: Record<string, { label: string; ring: string; bg: string; text: string }> = {
  gold:   { label: 'Gold',   ring: 'ring-[#C8A84B]', bg: 'bg-[#FEF9E7]', text: 'text-[#B8983B]' },
  silver: { label: 'Silver', ring: 'ring-slate-400',  bg: 'bg-slate-50',  text: 'text-slate-500'  },
  bronze: { label: 'Bronze', ring: 'ring-orange-400', bg: 'bg-orange-50', text: 'text-orange-500' },
  none:   { label: '',       ring: 'ring-border',     bg: 'bg-muted/30',  text: ''                },
}

export default function PublicProfile() {
  const { slug } = useParams<{ slug: string }>()

  const { data: profile, isLoading, isError } = useQuery({
    queryKey: ['public-profile', slug],
    queryFn: async () => {
      const { data, error } = await (supabase
        .from('journalist_profiles' as any)
        .select(`
          id, full_name, slug, email, bio, headline, nationality,
          beats, contact_visible_everyone, avatar_url,
          social_x, social_linkedin, social_instagram,
          profile_public
        `)
        .eq('slug', slug)
        .eq('profile_public', true)
        .maybeSingle() as any)
      if (error) throw error
      return data as any
    },
    enabled: !!slug,
  })

  const { data: accreditations } = useQuery({
    queryKey: ['public-accreditations', profile?.id],
    enabled: !!profile?.id,
    queryFn: async () => {
      const { data } = await (supabase
        .from('journalist_accreditations' as any)
        .select('id, status, accredited_at, organizations(id, name, slug)')
        .eq('journalist_id', profile.id)
        .eq('status', 'active') as any)
      return (data || []) as any[]
    },
  })

  const { data: publicStats } = useQuery({
    queryKey: ['public-stats', profile?.id],
    enabled: !!profile?.id,
    queryFn: async () => {
      const { data } = await (supabase
        .from('journalist_public_stats' as any)
        .select('total_submissions, published_articles, accredited_orgs')
        .eq('journalist_profile_id', profile.id)
        .maybeSingle() as any)
      return data as any
    },
  })

  const { data: scoreData } = useQuery({
    queryKey: ['public-score', profile?.id],
    enabled: !!profile?.id,
    queryFn: async () => {
      const { data } = await (supabase
        .from('journalist_coverage_scores' as any)
        .select('total_score, badge_tier, article_count, last_computed_at')
        .eq('journalist_id', profile.id)
        .maybeSingle() as any)
      return data as any
    },
  })

  if (isLoading) return <ConorbitLoader variant="page" />;

  if (isError || !profile) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4">
        <p className="text-justify text-muted-foreground text-sm">This profile is not publicly available.</p>
        <Link to="/" className="text-sm text-[#C8A84B] hover:underline">← Back to CONORBIT</Link>
      </div>
    )
  }

  const badge = BADGE_CONFIG[scoreData?.badge_tier ?? 'none']
  const initials = profile.full_name.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase()

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0D2240] to-[#1A3A5C] py-10 px-4">
      {/* CONORBIT header */}
      <div className="max-w-2xl mx-auto mb-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 text-white/80 hover:text-white text-sm">
          <Sparkles className="w-4 h-4 text-[#C8A84B]" />
          <span className="font-bold text-[#C8A84B] tracking-widest text-xs uppercase">CONORBIT</span>
        </Link>
        <span className="text-white/40 text-xs">Verified journalist profile</span>
      </div>

      <div className="max-w-2xl mx-auto space-y-4">

        {/* Profile card */}
        <div className="bg-card border border-white/10 rounded-2xl p-6">
          <div className="flex items-start gap-5">
            {/* Avatar */}
            <div className={`w-20 h-20 rounded-full ring-4 ${badge.ring} flex items-center justify-center flex-shrink-0 overflow-hidden bg-[#0D2240]`}>
              {profile.avatar_url ? (
                <img src={profile.avatar_url} alt={profile.full_name} className="w-full h-full object-cover" />
              ) : (
                <span className="text-white text-2xl font-bold">{initials}</span>
              )}
            </div>

            <div className="flex-1 min-w-0">
              <h1 className="text-[22px] font-bold">{profile.full_name}</h1>
              {profile.headline && (
                <p className="text-justify text-[13px] text-muted-foreground mt-0.5">{profile.headline}</p>
              )}
              <div className="flex flex-wrap items-center gap-3 mt-2">
                {profile.nationality && (
                  <span className="flex items-center gap-1 text-[11.5px] text-muted-foreground">
                    <MapPin size={11} /> {profile.nationality}
                  </span>
                )}
                {scoreData && scoreData.badge_tier !== 'none' && (
                  <span className={`text-[10.5px] font-semibold px-2 py-0.5 rounded-full ${badge.bg} ${badge.text}`}>
                    {badge.label} · {scoreData.total_score} pts
                  </span>
                )}
              </div>

              {/* Socials */}
              <div className="flex items-center gap-3 mt-3">
                {profile.social_x && (
                  <a href={`https://x.com/${profile.social_x.replace('@', '')}`} target="_blank" rel="noopener noreferrer"
                    className="text-muted-foreground hover:text-foreground transition-colors">
                    <Twitter size={15} />
                  </a>
                )}
                {profile.social_linkedin && (
                  <a href={profile.social_linkedin} target="_blank" rel="noopener noreferrer"
                    className="text-muted-foreground hover:text-foreground transition-colors">
                    <Linkedin size={15} />
                  </a>
                )}
                {profile.social_instagram && (
                  <a href={`https://instagram.com/${profile.social_instagram.replace('@', '')}`} target="_blank" rel="noopener noreferrer"
                    className="text-muted-foreground hover:text-foreground transition-colors">
                    <Instagram size={15} />
                  </a>
                )}
                {profile.contact_visible_everyone && profile.email && (
                  <a href={`mailto:${profile.email}`}
                    className="text-[11px] text-[#C8A84B] hover:underline flex items-center gap-1">
                    <ExternalLink size={11} /> Email journalist
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* Bio */}
          {profile.bio && (
            <p className="mt-4 text-[13px] text-muted-foreground text-justify leading-relaxed border-t border-border pt-4">
              {profile.bio}
            </p>
          )}

          {/* Beats */}
          {profile.beats && profile.beats.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-1.5">
              {(profile.beats as string[]).map((beat: string) => (
                <span key={beat} className="text-[11px] bg-muted/60 px-2.5 py-1 rounded-full text-muted-foreground">
                  {beat}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Activity counters */}
        {publicStats && (publicStats.total_submissions > 0 || publicStats.published_articles > 0) && (
          <div className="bg-card border border-white/10 rounded-2xl p-5">
            <div className="grid grid-cols-3 divide-x divide-border text-center">
              <div className="px-4">
                <div className="text-[26px] font-bold">{publicStats.total_submissions ?? 0}</div>
                <div className="flex items-center justify-center gap-1 text-[11px] text-muted-foreground mt-0.5">
                  <FileText size={11} /> Submissions
                </div>
              </div>
              <div className="px-4">
                <div className="text-[26px] font-bold">{publicStats.published_articles ?? 0}</div>
                <div className="flex items-center justify-center gap-1 text-[11px] text-muted-foreground mt-0.5">
                  <Newspaper size={11} /> Articles
                </div>
              </div>
              <div className="px-4">
                <div className="text-[26px] font-bold">{publicStats.accredited_orgs ?? 0}</div>
                <div className="flex items-center justify-center gap-1 text-[11px] text-muted-foreground mt-0.5">
                  <ShieldCheck size={11} /> Accreditations
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Coverage score card */}
        {scoreData && (
          <div className="bg-card border border-white/10 rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <Award size={15} className="text-[#C8A84B]" />
              <h2 className="text-[13px] font-semibold">Coverage Score</h2>
            </div>
            <div className="flex items-end gap-4">
              <div>
                <div className="text-[36px] font-bold leading-none">{scoreData.total_score}</div>
                <div className="text-[11px] text-muted-foreground mt-0.5">/ 1000 points</div>
              </div>
              {scoreData.badge_tier && scoreData.badge_tier !== 'none' && (
                <span className={`text-[12px] font-semibold px-3 py-1 rounded-full mb-1 ${badge.bg} ${badge.text}`}>
                  {badge.label} journalist
                </span>
              )}
            </div>
            {scoreData.article_count > 0 && (
              <p className="text-justify text-[11.5px] text-muted-foreground mt-2">
                Based on {scoreData.article_count} verified article{scoreData.article_count !== 1 ? 's' : ''} on CONORBIT
              </p>
            )}
          </div>
        )}

        {/* Accreditations */}
        {accreditations && accreditations.length > 0 && (
          <div className="bg-card border border-white/10 rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <ShieldCheck size={15} className="text-[#006B3C]" />
              <h2 className="text-[13px] font-semibold">Active Accreditations</h2>
            </div>
            <div className="space-y-2">
              {(accreditations as any[]).map((acc: any) => (
                <div key={acc.id} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                  <div>
                    <div className="text-[13px] font-medium">{acc.organizations?.name}</div>
                    {acc.accredited_at && (
                      <div className="text-[11px] text-muted-foreground">
                        Since {new Date(acc.accredited_at).toLocaleDateString('en', { month: 'long', year: 'numeric' })}
                      </div>
                    )}
                  </div>
                  <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-[#E1F5EE] text-[#0F6E56]">
                    Active
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Powered by */}
        <div className="text-center py-2">
          <p className="text-justify text-[11px] text-white/30">
            Verified by{' '}
            <Link to="/" className="text-[#C8A84B]/70 hover:text-[#C8A84B] transition-colors">
              CONORBIT
            </Link>
            {' '}, the accreditation platform for parliamentary journalists
          </p>
        </div>
      </div>
    </div>
  )
}
